"use client";

import { useTheme } from "next-themes";
import { useEffect } from "react";

/**
 * Mirrors the app's resolved theme into Docusaurus's localStorage key so that
 * the docs pages opened in the same browser profile stay in sync with the
 * app's light/dark preference.
 *
 * - App theme (next-themes):  localStorage["theme"]       → "light" | "dark" | "system"
 * - Docusaurus theme:         localStorage["theme-radium"] → "light" | "dark"
 *
 * We write the *resolved* theme (never "system") so Docusaurus always receives
 * an explicit light/dark directive rather than inheriting its own default.
 *
 * Guard: only write when the value actually changes. Writing the same value
 * still dispatches a storage event to other tabs in some browsers, which would
 * feed back into RadiumThemeSyncBridge on any open docs tab and trigger a loop.
 * The equality check ensures we only fire a storage event on a genuine change.
 *
 * Key stability: the "theme-radium" key is the Docusaurus localStorage key
 * with a pinned namespace ("radium") set in docusaurus.config.ts. Without the
 * pin, Docusaurus hashes the site URL to produce the suffix, which differs
 * between dev ("theme-295") and production ("theme-625"), breaking sync.
 */
export function ThemeSyncBridge() {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (resolvedTheme === "light" || resolvedTheme === "dark") {
      if (localStorage.getItem("theme-radium") !== resolvedTheme) {
        localStorage.setItem("theme-radium", resolvedTheme);
      }
    }
  }, [resolvedTheme]);

  return null;
}
